import {
    useState,
    useReducer,
    useEffect,
    SyntheticEvent,
    KeyboardEvent,
    useMemo,
    useCallback,
    startTransition,
    useRef,
} from 'react';
import localforage from 'localforage';
import { Box, FormGroup, FormLabel } from '@mui/material';
import {
    GridColDef,
    GridActionsCellItem,
    GridPaginationModel,
    GridColumnVisibilityModel,
    GridRenderCellParams,
    GridSortModel,
} from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { FormControl } from '@mui/material';
import { Visibility, Devices, FilterList } from '@mui/icons-material';
import '@/styles/pages/Device/DevicePage.scss';
import BaseCard from '@/components/ui/BaseCard';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseTextField from '@/components/form/BaseTextField';
import StoreFilterBlock from '@/components/store/StoreFilterBlock';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import { useAppContext } from '@/provider/contexts';
import { fetchDeviceList } from '@/api/device';
import {
    DevicePageState,
    DevicePageAction,
    SortModelType,
    DeviceDetail,
    DeviceQuery,
    DeviceItem,
} from '@/types/pages/device';
import BaseSelect from '@/components/form/BaseSelect';
import BaseAlert from '@/components/feedback/BaseAlert';
import StoreAutocomplete from '@/components/store/StoreAutocomplete';
import PopoverMenu from '@/components/ui/PopoverMenu';
import BaseTableColumn from '@/components/ui/BaseTableColumn';
import BaseTableColumnContent from '@/components/ui/BaseTableColumnContent';
import DateTimeRangePicker from '@/components/form/DateTimeRangePicker';
import { utcDateStringToLocalDateFormat } from '@/utils/formatUtils';
import { isEmpty, uniq } from 'lodash';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useCorporationSettings } from '@/mobile/hooks/useCorporationSettings';
import DeviceDetailDialog from '@/components/device/DeviceDetailDialog';

function reducer(state: DevicePageState, action: DevicePageAction): DevicePageState {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_STATUS_DATE_TIME_PICKER_AND_QUERY':
            return {
                ...state,
                statusDateTimePickerData: {
                    ...state.statusDateTimePickerData,
                    startTime: action.payload.startTime,
                    endTime: action.payload.endTime,
                },
            };
        case 'SET_UPDATE_DATE_TIME_PICKER_AND_QUERY':
            return {
                ...state,
                updateDateTimePickerData: {
                    ...state.updateDateTimePickerData,
                    startTime: action.payload.startTime,
                    endTime: action.payload.endTime,
                },
            };
        case 'SET_SEARCH_QUERY':
            return { ...state, searchQuery: action.payload };
        case 'SET_SORT_MODEL':
            return {
                ...state,
                sortModel: action.payload,
            };
        case 'SET_DEVICES':
            return {
                ...state,
                devices: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_PRODUCT_ASSOCIATION_DISABLED':
            return {
                ...state,
                isProductAssociationDisabled: action.payload,
                // searchQuery: { ...state.searchQuery, f_product: action.payload ? 'NO_ASSOCIATION' : '' },
            };
        case 'SET_PARENT_MAC_DISABLED':
            return {
                ...state,
                isParentMacDisabled: action.payload,
                // searchQuery: {
                //     ...state.searchQuery,
                //     f_parent_mac: action.payload ? 'NO_PARENT' : '',
                // },
            };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        default:
            return state;
    }
}

const statusFilterOptions = ['OFFLINE', 'ONLINE', 'RECONNECTION'];
const typeFilterOptions = [
    { label: 'AP', value: 2 },
    { label: 'RT', value: 1 },
    { label: 'TAG', value: 3 },
];

const JobStatusMap = {
    All: '',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    PROCESSING: 'PROCESSING',
};

const getJobStatusOptions = (t: (key: string) => string) => {
    return Object.entries(JobStatusMap).map(([key, value]) => ({
        value,
        label: t(key),
    }));
};

const availabilityOptions = [
    { value: 'All', label: 'All' },
    { value: '1', label: 'Available' },
    { value: '0', label: 'Not Available' },
];

const ALL_VALUE = '';
const CONCRETE_STATUSES = ['Success', 'Failed', 'Processing'] as const;

type JobStatus = (typeof CONCRETE_STATUSES)[number] | typeof ALL_VALUE;

const normalizeJobStatuses = (input: string[]): string => {
    if (input.length === 0) {
        return ALL_VALUE;
    }

    const lastSelected = input[input.length - 1];
    const set = new Set(input);

    if (lastSelected === ALL_VALUE) {
        return ALL_VALUE;
    }

    set.delete(ALL_VALUE);

    const concrete = Array.from(set) as JobStatus[];

    if (concrete.length === CONCRETE_STATUSES.length) {
        return ALL_VALUE;
    }

    return concrete.join(',');
};

type SearchField = keyof DevicePageState['searchQuery'];

const defaultState: DevicePageState = {
    loading: true,
    statusDateTimePickerData: {
        startTime: null,
        endTime: null,
    },
    updateDateTimePickerData: {
        startTime: null,
        endTime: null,
    },
    searchQuery: {
        f_store: undefined,
        f_mac: '',
        f_product: '',
        f_parent_mac: '',
        f_profile: '',
        f_status_time: '',
        f_last_job_status: '',
        f_update_time: '',
        f_battery_lv: '',
        f_rssi: '',
        f_fw_version: '',
        f_layout: '',
        f_device_type: '',
        f_ed_status: '',
        f_in_rfu: '',
        f_whitelist: 'All',
    },
    sortModel: {} as SortModelType,
    isProductAssociationDisabled: false,
    isParentMacDisabled: false,
    devices: [],
    rowCount: 0,
    paginationModel: {
        page: 0,
        pageSize: 5,
    },
};

export default function DevicePage() {
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const location = useLocation();
    const [state, dispatch] = useReducer(reducer, defaultState);
    const { from } = location.state || {};
    const [deviceDetailDialogOpen, setDeviceDetailDialogOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [deviceId, setDeviceId] = useState('');
    const [viewType, setViewType] = useState<'ap' | 'rt' | 'tag' | ''>('');

    const [searchForm, setSearchForm] = useState<DevicePageState['searchQuery']>(state.searchQuery);
    const [statusTimeLocal, setStatusTimeLocal] = useState<{
        startTime: dayjs.Dayjs | null;
        endTime: dayjs.Dayjs | null;
    }>({
        startTime: state.statusDateTimePickerData.startTime,
        endTime: state.statusDateTimePickerData.endTime,
    });
    const [updateTimeLocal, setUpdateTimeLocal] = useState<{
        startTime: dayjs.Dayjs | null;
        endTime: dayjs.Dayjs | null;
    }>({
        startTime: state.updateDateTimePickerData.startTime,
        endTime: state.updateDateTimePickerData.endTime,
    });

    const searchFormRef = useRef(searchForm);
    searchFormRef.current = searchForm;
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    const assetFeatureRef = useRef(false);
    const { fetchCorporationSettings, isTimezoneReady, formatUtcToStoreTime, storeWallTimeToUtcTimestamp } =
        useCorporationSettings();

    useEffect(() => {
        setSearchForm(state.searchQuery);
        setStatusTimeLocal({
            startTime: state.statusDateTimePickerData.startTime,
            endTime: state.statusDateTimePickerData.endTime,
        });
        setUpdateTimeLocal({
            startTime: state.updateDateTimePickerData.startTime,
            endTime: state.updateDateTimePickerData.endTime,
        });
    }, [state.searchQuery, state.statusDateTimePickerData, state.updateDateTimePickerData]);

    const updateSearchForm = useCallback((field: SearchField, value: string | string[]) => {
        setSearchForm((prev) => {
            const updated = { ...prev, [field]: value };
            searchFormRef.current = updated;
            return updated;
        });
    }, []);

    const assetFilterDisable = useMemo(() => {
        const { devices } = state;
        return devices.length > 0 && devices[0].asset_flag === 2;
    }, [state.devices]);

    const jobStatusOptions = useMemo(() => getJobStatusOptions(t), [t]);

    const deriveQueryPayload = (effectiveQuery: DeviceQuery): Partial<DeviceQuery> => {
        const payload: Record<string, unknown> = {};
        Object.entries(effectiveQuery).forEach(([key, value]) => {
            if (value === '') return;
            if (value === undefined || value === null) return;

            payload[key] = value;
        });

        if (payload['f_whitelist'] === 'All') {
            delete payload['f_whitelist'];
        }

        return payload;
    };

    const fetchDevices = useCallback(
        async (queryOverride?: DevicePageState['searchQuery']) => {
            fetchAbortControllerRef.current?.abort();
            const fetchId = latestFetchIdRef.current + 1;
            latestFetchIdRef.current = fetchId;

            const abortController = new AbortController();
            fetchAbortControllerRef.current = abortController;
            dispatch({ type: 'SET_LOADING', payload: true });

            try {
                const corporationId = (await localforage.getItem('corporation_id')) as number;
                const { field, sort } = state.sortModel;
                const effectiveQuery = queryOverride ?? state.searchQuery;

                const queryPayload = {
                    ...deriveQueryPayload(effectiveQuery),
                    data_per_page: state.paginationModel.pageSize,
                    current_page: state.paginationModel.page + 1,
                    sort_field: field,
                    sort_order: sort,
                };
                if (state.isProductAssociationDisabled) {
                    queryPayload.f_product = '!!NO_ASSOCIATION';
                }
                if (state.isParentMacDisabled) {
                    queryPayload.f_parent_mac = '!!NO_PARENT';
                }

                const {
                    data: { data, isSuccess, message },
                } = await fetchDeviceList(corporationId, queryPayload, { signal: abortController.signal });

                if (latestFetchIdRef.current !== fetchId) return;
                if (isSuccess) {
                    const devices = data.aaData || [];
                    const sortDetailsByPageIndex = (details: DeviceItem['details']) => {
                        return [...details].sort((a, b) => a.page_idx - b.page_idx);
                    };

                    const mapDeviceToRow = (device: DeviceItem): DeviceItem => {
                        return {
                            ...device,
                            ed_status_time: formatUtcToStoreTime(device.ed_status_time),
                            details: sortDetailsByPageIndex(device.details).map((detail) => ({
                                ...detail,
                                last_job_status_time: formatUtcToStoreTime(detail.last_job_status_time),
                            })),
                        };
                    };
                    const deviceRows = devices.map(mapDeviceToRow);
                    dispatch({
                        type: 'SET_DEVICES',
                        payload: {
                            ...data,
                            aaData: deviceRows,
                        },
                    });
                } else {
                    setNotification({
                        message: message,
                        severity: 'error',
                    });
                }
            } catch (error) {
                if (abortController.signal.aborted) return;
                console.error('An unexpected error occurred:', error);
                setNotification({
                    message: t('An unexpected error occurred'),
                    severity: 'error',
                });
            } finally {
                if (latestFetchIdRef.current === fetchId) {
                    requestAnimationFrame(() => {
                        dispatch({ type: 'SET_LOADING', payload: false });
                        fetchAbortControllerRef.current = null;
                    });
                }
            }
        },
        [
            state.sortModel,
            state.searchQuery,
            state.paginationModel.pageSize,
            state.paginationModel.page,
            state.isProductAssociationDisabled,
            state.isParentMacDisabled,
            formatUtcToStoreTime,
            setNotification,
            t,
        ],
    );

    const clearDevices = () => {
        const payload = {
            ...searchForm,
            f_store: undefined,
            f_mac: '',
            f_product: '',
            f_parent_mac: '',
            f_profile: '',
            f_status_time: '',
            f_last_job_status: '',
            f_update_time: '',
            f_battery_lv: '',
            f_rssi: '',
            f_fw_version: '',
            f_layout: '',
            f_in_rfu: '',
            f_whitelist: assetFeatureRef.current ? '1' : 'All',
            // f_device_type: '',
            // f_ed_status: '',
        };
        setSearchForm(payload);
        dispatch({
            type: 'SET_SEARCH_QUERY',
            payload,
        });
        dispatch({
            type: 'SET_STATUS_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: null,
                endTime: null,
            },
        });
        dispatch({
            type: 'SET_UPDATE_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: null,
                endTime: null,
            },
        });
        dispatch({
            type: 'SET_PARENT_MAC_DISABLED',
            payload: false,
        });
        dispatch({
            type: 'SET_PRODUCT_ASSOCIATION_DISABLED',
            payload: false,
        });
        dispatch({
            type: 'SET_PAGINATION_MODEL',
            payload: { page: 0, pageSize: 5 },
        });
        dispatch({
            type: 'SET_SORT_MODEL',
            payload: { field: undefined, sort: null },
        });
    };

    const errorCodeCellRenderer = useCallback(({ row }: GridRenderCellParams) => {
        const detail = (row.details as DeviceDetail[]).find((d) => d.page_idx === row.current_page);
        return <BaseTableColumn columnValue={detail?.error_code ?? '-'} />;
    }, []);

    const columnVisibilityModelHandler = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const formatToTimestamp = useCallback(
        (startTime: dayjs.Dayjs | null, endTime: dayjs.Dayjs | null) => {
            if (startTime && endTime) {
                const start = storeWallTimeToUtcTimestamp(startTime);
                const end = storeWallTimeToUtcTimestamp(endTime);
                return `${start}_${end}`;
            }
            return '';
        },
        [storeWallTimeToUtcTimestamp],
    );

    const applySearchAndFetch = useCallback(async () => {
        const currentSearchForm = {
            ...searchFormRef.current,
            f_status_time: formatToTimestamp(statusTimeLocal.startTime, statusTimeLocal.endTime),
            f_update_time: formatToTimestamp(updateTimeLocal.startTime, updateTimeLocal.endTime),
        };
        dispatch({ type: 'SET_SEARCH_QUERY', payload: currentSearchForm });
        dispatch({
            type: 'SET_STATUS_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: statusTimeLocal.startTime,
                endTime: statusTimeLocal.endTime,
            },
        });
        dispatch({
            type: 'SET_UPDATE_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: updateTimeLocal.startTime,
                endTime: updateTimeLocal.endTime,
            },
        });
        await fetchDevices(currentSearchForm);
    }, [
        fetchDevices,
        formatToTimestamp,
        statusTimeLocal.endTime,
        statusTimeLocal.startTime,
        updateTimeLocal.endTime,
        updateTimeLocal.startTime,
    ]);

    const keyUpHandler = useCallback(
        (event: KeyboardEvent<Element>) => {
            if (event.key === 'Enter') {
                applySearchAndFetch();
            }
        },
        [applySearchAndFetch],
    );

    const pageChangeHandler = useCallback(
        (pageModel: GridPaginationModel) => {
            dispatch({
                type: 'SET_PAGINATION_MODEL',
                payload: state.paginationModel.pageSize !== pageModel.pageSize ? { ...pageModel, page: 0 } : pageModel,
            });
        },
        [dispatch, state.paginationModel.pageSize],
    );

    const handleTypeFilterChange = useCallback(
        (e: SyntheticEvent, checked: boolean) => {
            const value = (e.target as HTMLInputElement).value;
            const checkedTypes = searchForm.f_device_type !== '' ? searchForm.f_device_type.split(',') : [];

            const updateTypes = checked ? [...checkedTypes, value] : checkedTypes.filter((type) => type !== value);
            const newValue = updateTypes.join(',');
            const updatedSearchForm = { ...searchForm, f_device_type: newValue };

            updateSearchForm('f_device_type', newValue);
            searchFormRef.current = updatedSearchForm;
            startTransition(() => {
                dispatch({ type: 'SET_SEARCH_QUERY', payload: updatedSearchForm });
            });
            fetchDevices(updatedSearchForm);
        },
        [searchForm, updateSearchForm, fetchDevices, dispatch],
    );

    const handleStatusFilterChange = useCallback(
        (e: SyntheticEvent, checked: boolean) => {
            const value = (e.target as HTMLInputElement).value;
            const checkedStatuses = searchForm.f_ed_status !== '' ? searchForm.f_ed_status.split(',') : [];
            const updateStatuses = checked
                ? [...checkedStatuses, value]
                : checkedStatuses.filter((status) => status !== value);
            const newValue = updateStatuses.join(',');

            const updatedSearchForm = { ...searchForm, f_ed_status: newValue };
            updateSearchForm('f_ed_status', newValue);
            searchFormRef.current = updatedSearchForm;

            startTransition(() => {
                dispatch({ type: 'SET_SEARCH_QUERY', payload: updatedSearchForm });
            });
            fetchDevices(updatedSearchForm);
        },
        [searchForm, updateSearchForm, fetchDevices, dispatch],
    );
    const productCellRenderer = useCallback(({ row }: GridRenderCellParams, key: keyof DeviceDetail) => {
        if (isEmpty(row.details)) return <BaseTableColumn>-</BaseTableColumn>;

        if (key === 'last_job_status' || key === 'last_job_status_time') {
            const list = row.details.map((o: DeviceDetail) => o[key]) || [];
            const status = uniq(list)[0] ?? '-';

            return (
                <BaseTableColumn>
                    <BaseTableColumnContent columnValue={status as string} />
                </BaseTableColumn>
            );
        }
        if (key === 'product_barcode' || key === 'product_name') {
            return (
                <BaseTableColumn>
                    {row.details.map((detail: DeviceDetail, index: number) => {
                        if (detail.page_idx === 1 || detail.product_id !== null) {
                            return <BaseTableColumnContent key={index} columnValue={(detail[key] as string) || '-'} />;
                        }
                    })}
                </BaseTableColumn>
            );
        }

        return (
            <BaseTableColumn>
                {row.details.map((detail: DeviceDetail, index: number) => (
                    <BaseTableColumnContent
                        key={index}
                        columnValue={utcDateStringToLocalDateFormat(detail[key] as string)}
                    />
                ))}
            </BaseTableColumn>
        );
    }, []);

    const layoutCellRenderer = useCallback(({ row }: GridRenderCellParams) => {
        const layoutItem = row.details.filter(
            (detail: DeviceDetail) => row.current_page === detail.page_idx,
        ) as DeviceDetail[];

        if (layoutItem.length === 0) return <BaseTableColumn columnValue={'-'} />;
        return (
            <BaseTableColumn>
                {layoutItem.map((o, index) => (
                    <BaseTableColumnContent key={index} columnValue={o.layout_name || '-'} />
                ))}
            </BaseTableColumn>
        );
    }, []);

    // Memoized simple cell renderers to avoid creating new functions on every render
    const simpleCellRenderer = useCallback((param: GridRenderCellParams) => {
        return <BaseTableColumn columnValue={param.value} />;
    }, []);

    const simpleCellRendererWithDefault = useCallback((param: GridRenderCellParams) => {
        return <BaseTableColumn columnValue={param.value || '-'} />;
    }, []);

    const deviceTypeCellRenderer = useCallback((param: GridRenderCellParams) => {
        return <BaseTableColumn columnValue={param.value?.toUpperCase()} />;
    }, []);

    const batteryCellRenderer = useCallback((param: GridRenderCellParams) => {
        return param.row.device_type_id === 3 ? (
            <BaseTableColumn columnValue={param.value || '-'} />
        ) : (
            <BaseTableColumn columnValue={''} />
        );
    }, []);

    const assetFlagCellRenderer = useCallback((param: GridRenderCellParams) => {
        return (
            <Box
                display="flex"
                alignItems="center"
                height={'100%'}
                sx={{
                    pt: '14px',
                    pb: '13px',
                }}
            >
                {param.value === 0 ? (
                    <CloseIcon color="error" />
                ) : param.value === 1 ? (
                    <CheckIcon color="success" />
                ) : (
                    ''
                )}
            </Box>
        );
    }, []);

    const getSortField = useCallback((key: string) => {
        switch (key) {
            case 'product_name':
                return 'details.product_name';
            case 'product_barcode':
                return 'details.product_barcode';
            case 'last_job_status':
                return 'details.last_job_status';
            case 'last_job_status_time':
                return 'details.last_job_status_time';
            case 'layout_id':
                return 'details.layout_name';
            case 'error_code':
                return 'status_code';
            default:
                return key;
        }
    }, []);

    const sortModelChangeHandler = useCallback(
        (sortModel: GridSortModel) => {
            if (sortModel.length > 0) {
                const { field, sort } = sortModel[0];
                const fieldKey = getSortField(field);
                startTransition(() => {
                    dispatch({
                        type: 'SET_SORT_MODEL',
                        payload: { field: fieldKey, sort: sort ?? null },
                    });
                });
            }
        },
        [dispatch, getSortField],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'store_name',
                headerName: t('Store'),
                flex: 1,
                renderCell: simpleCellRenderer,
            },
            {
                field: 'mac',
                headerName: 'MAC',
                flex: 1,
                renderCell: simpleCellRenderer,
            },
            {
                field: 'product_barcode',
                headerName: t('Product Barcode'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'product_barcode'),
            },
            {
                field: 'product_name',
                headerName: t('Product Name'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'product_name'),
            },
            {
                field: 'parent_mac',
                headerName: t('Parent MAC'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'profile_name',
                headerName: t('Profile'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'device_type_name',
                headerName: t('Type'),
                flex: 1,
                renderCell: deviceTypeCellRenderer,
            },
            {
                field: 'ed_status',
                headerName: t('Status'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'ed_status_time',
                headerName: t('Status Time'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'last_job_status',
                headerName: t('Job Status'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'last_job_status'),
            },
            {
                field: 'last_job_status_time',
                headerName: t('Update Time'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'last_job_status_time'),
            },
            {
                field: 'battery_lv',
                headerName: t('Battery Level'),
                flex: 1,
                renderCell: batteryCellRenderer,
            },
            {
                field: 'rssi',
                headerName: t('RSSI'),
                flex: 1,
                renderCell: simpleCellRenderer,
            },
            {
                field: 'fw_version',
                headerName: t('FW Version'),
                flex: 1,
                renderCell: simpleCellRenderer,
            },
            {
                field: 'layout_id',
                headerName: t('Layout'),
                flex: 1,
                renderCell: (param) => layoutCellRenderer(param),
            },
            {
                field: 'status_code',
                headerName: t('Error Code'),
                flex: 1,
                renderCell: errorCodeCellRenderer,
            },
            {
                field: 'asset_flag',
                headerName: t('Available'),
                flex: 1,
                sortable: false,
                renderCell: assetFlagCellRenderer,
            },
            // {
            //     field: 'in_rfu',
            //     headerName: 'RFU status',
            //     sortable: false,
            //     flex: 1,
            //     renderCell: (param) => <BaseTableColumn columnValue={param.value === 1 ? 'In-Progress' : '-'} />,
            // },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                flex: 1,
                hideable: false,
                disableColumnMenu: false,
                getActions: ({ row }) => [
                    <GridActionsCellItem
                        icon={<Visibility />}
                        label={t('View')}
                        sx={{ color: 'primary.main' }}
                        onClick={() => {
                            setDeviceDetailDialogOpen(true);
                            setViewType(row.device_type_name);
                            setDeviceId(row.mac);
                        }}
                    />,
                ],
            },
        ],
        [
            assetFlagCellRenderer,
            batteryCellRenderer,
            deviceTypeCellRenderer,
            errorCodeCellRenderer,
            layoutCellRenderer,
            productCellRenderer,
            simpleCellRenderer,
            simpleCellRendererWithDefault,
            t,
        ],
    );

    const getRowId = useCallback((row: { mac?: string; device_type_id?: number }) => {
        if (row.mac) return row.mac;
        if (typeof row.device_type_id === 'number') return row.device_type_id;
        return '';
    }, []);

    const localeText = useMemo(() => ({ noRowsLabel: t('No devices') }), [t]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        const initialize = async () => {
            dispatch({ type: 'SET_LOADING', payload: true });
            const settings = await fetchCorporationSettings();
            if (settings) {
                assetFeatureRef.current = settings.asset_list_feature === '1';
            }
            const baseQuery = { ...state.searchQuery, f_whitelist: assetFeatureRef.current ? '1' : 'All' };
            switch (from) {
                case 'device-ratio':
                    baseQuery.f_device_type = '3';
                    baseQuery.f_ed_status = 'OFFLINE';
                    setColumnVisibilityModel((prev) => ({
                        ...prev,
                        asset_flag: true,
                    }));
                    break;

                case 'job-ratio':
                    baseQuery.f_device_type = '3';
                    baseQuery.f_last_job_status = `${JobStatusMap.PROCESSING},${JobStatusMap.FAILED}`;
                    setColumnVisibilityModel((prev) => ({
                        ...prev,
                        asset_flag: true,
                    }));
                    break;

                case 'ap-ratio':
                    baseQuery.f_device_type = '2';
                    baseQuery.f_ed_status = 'OFFLINE';
                    break;

                default:
                    break;
            }

            dispatch({
                type: 'SET_SEARCH_QUERY',
                payload: baseQuery,
            });
        };
        initialize();

        return () => {
            window.history.replaceState({}, '');
        };
    }, [from, fetchCorporationSettings]);

    useEffect(() => {
        if (!isTimezoneReady) return;
        fetchDevices();
    }, [isTimezoneReady, state.paginationModel, state.sortModel]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        store_name: true,
        mac: true,
        product_barcode: true,
        product_name: true,
        parent_mac: true,
        profile_name: true,
        device_type_name: true,
        ed_status: true,
        ed_status_time: true,
        last_job_status: true,
        last_job_status_time: false,
        battery_lv: false,
        rssi: false,
        fw_version: false,
        layout_id: false,
        error_code: false,
        asset_flag: false,
        // in_rfu: false,
        action: true,
    });
    const tableElement = useMemo(
        () => (
            <BaseDataTable
                rows={state.devices}
                columns={columns}
                paginationModel={state.paginationModel}
                loading={state.loading}
                paginationMode="server"
                sortingMode="server"
                onSortModelChange={sortModelChangeHandler}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={columnVisibilityModelHandler}
                rowCount={state.rowCount || 0}
                getRowId={getRowId}
                localeText={localeText}
                onPaginationModelChange={pageChangeHandler}
            />
        ),
        [
            state.devices,
            columns,
            state.paginationModel,
            state.loading,
            sortModelChangeHandler,
            columnVisibilityModel,
            columnVisibilityModelHandler,
            state.rowCount,
            getRowId,
            localeText,
            pageChangeHandler,
        ],
    );

    return (
        <>
            {alertMessage && (
                <BaseAlert
                    message={alertMessage}
                    severity="info"
                    onClose={() => setAlertMessage(null)}
                    style={{ marginBottom: '1rem' }}
                />
            )}
            <BasePaper className="device-page">
                <BaseTitle icon={<Devices />} title={t('Devices')} />
                <StoreFilterBlock handleSearch={applySearchAndFetch} handleClear={clearDevices}>
                    {columnVisibilityModel.store_name && (
                        <StoreAutocomplete
                            storeId={state.searchQuery.f_store}
                            onStoreChange={(storeId) => {
                                dispatch({
                                    type: 'SET_SEARCH_QUERY',
                                    payload: {
                                        ...state.searchQuery,
                                        f_store: storeId,
                                    },
                                });
                            }}
                        />
                    )}
                    {columnVisibilityModel.mac && (
                        <BaseTextField
                            label={t('MAC')}
                            value={searchForm.f_mac}
                            onChange={(e) => updateSearchForm('f_mac', e.target.value)}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {(columnVisibilityModel.product_barcode || columnVisibilityModel.product_name) && (
                        <Box className="field-with-checkbox">
                            <BaseTextField
                                fullWidth
                                label={t('Product (Barcode/Name)')}
                                value={searchForm.f_product}
                                disabled={state.isProductAssociationDisabled}
                                onChange={(e) => updateSearchForm('f_product', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                            <FormControlLabel
                                label={t('No Product Association')}
                                control={
                                    <Checkbox
                                        checked={state.isProductAssociationDisabled}
                                        onChange={(e) => {
                                            dispatch({
                                                type: 'SET_PRODUCT_ASSOCIATION_DISABLED',
                                                payload: e.target.checked,
                                            });
                                            updateSearchForm('f_product', '');
                                        }}
                                    />
                                }
                            />
                        </Box>
                    )}
                    {columnVisibilityModel.parent_mac && (
                        <Box className="field-with-checkbox">
                            <BaseTextField
                                fullWidth
                                label={t('Parent MAC')}
                                value={searchForm.f_parent_mac}
                                disabled={state.isParentMacDisabled}
                                onChange={(e) => updateSearchForm('f_parent_mac', e.target.value)}
                                onKeyUp={keyUpHandler}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={state.isParentMacDisabled}
                                        onChange={(e) => {
                                            dispatch({
                                                type: 'SET_PARENT_MAC_DISABLED',
                                                payload: e.target.checked,
                                            });
                                            updateSearchForm('f_parent_mac', '');
                                        }}
                                    />
                                }
                                label={t('No Parent MAC')}
                            />
                        </Box>
                    )}
                    {columnVisibilityModel.profile_name && (
                        <BaseTextField
                            label={t('Profile')}
                            value={searchForm.f_profile}
                            onChange={(e) => updateSearchForm('f_profile', e.target.value)}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.ed_status_time && (
                        <DateTimeRangePicker
                            label={t('Status Time Range')}
                            startTime={statusTimeLocal.startTime}
                            endTime={statusTimeLocal.endTime}
                            onChange={(startTime, endTime) => {
                                // Use startTransition to prevent UI blocking when picking dates
                                startTransition(() => {
                                    setStatusTimeLocal({ startTime, endTime });
                                });
                            }}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.last_job_status && (
                        <Box className="filter-dropdown">
                            <FormControl fullWidth onKeyDown={keyUpHandler}>
                                <BaseSelect
                                    label={t('Job Status')}
                                    multiple
                                    value={searchForm.f_last_job_status?.split(',')}
                                    onChange={(e) => {
                                        const rawValues = e.target.value as string[];
                                        if (rawValues.length === 0) {
                                            updateSearchForm('f_last_job_status', '');
                                            return;
                                        }
                                        const values = normalizeJobStatuses(rawValues);
                                        updateSearchForm('f_last_job_status', values);
                                    }}
                                    options={jobStatusOptions}
                                    renderValue={(selected) =>
                                        jobStatusOptions
                                            .filter((opt) => (selected as string[]).includes(opt.value))
                                            .map((opt) => opt.label)
                                            .join(', ')
                                    }
                                />
                            </FormControl>
                        </Box>
                    )}
                    {columnVisibilityModel.last_job_status_time && (
                        <DateTimeRangePicker
                            label={t('Update Time Range')}
                            startTime={updateTimeLocal.startTime}
                            endTime={updateTimeLocal.endTime}
                            onChange={(startTime, endTime) => {
                                // Use startTransition to prevent UI blocking when picking dates
                                startTransition(() => {
                                    setUpdateTimeLocal({ startTime, endTime });
                                });
                            }}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.battery_lv && (
                        <BaseTextField
                            label={t('Battery Level')}
                            value={searchForm.f_battery_lv}
                            onChange={(e) => updateSearchForm('f_battery_lv', e.target.value)}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.rssi && (
                        <BaseTextField
                            label={t('RSSI')}
                            value={searchForm.f_rssi}
                            onChange={(e) => updateSearchForm('f_rssi', e.target.value)}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.fw_version && (
                        <BaseTextField
                            label={t('FW Version')}
                            value={searchForm.f_fw_version}
                            onChange={(e) => updateSearchForm('f_fw_version', e.target.value)}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.layout_id && (
                        <BaseTextField
                            label={t('Layout')}
                            value={searchForm.f_layout}
                            onChange={(e) => updateSearchForm('f_layout', e.target.value)}
                            onKeyUp={keyUpHandler}
                        />
                    )}
                    {columnVisibilityModel.asset_flag && !assetFilterDisable && (
                        <Box className="filter-dropdown">
                            <FormControl fullWidth onKeyDown={keyUpHandler}>
                                <BaseSelect
                                    label={t('Device Availability')}
                                    value={searchForm.f_whitelist}
                                    onChange={(e) => updateSearchForm('f_whitelist', e.target.value as string)}
                                    options={availabilityOptions}
                                    renderValue={(value) => {
                                        const option = availabilityOptions.find((item) => item.value === value);
                                        return t(option?.label as string);
                                    }}
                                />
                            </FormControl>
                        </Box>
                    )}
                </StoreFilterBlock>
                <BaseCard className="table-card">
                    <div className="table-box">
                        <div className="actions-box">
                            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                                {columnVisibilityModel.device_type_name && (
                                    <PopoverMenu title={t('Type')} icon={<FilterList />}>
                                        <FormLabel sx={{ pl: 1.5 }}>{t('Type Filter')}</FormLabel>
                                        <FormGroup>
                                            {typeFilterOptions.map((option) => (
                                                <FormControlLabel
                                                    key={option.label}
                                                    control={<Checkbox />}
                                                    checked={searchForm.f_device_type.includes(option.value.toString())}
                                                    label={t(option.label)}
                                                    value={option.value}
                                                    onChange={handleTypeFilterChange}
                                                    sx={{ m: 0 }}
                                                />
                                            ))}
                                        </FormGroup>
                                    </PopoverMenu>
                                )}
                                {columnVisibilityModel.ed_status && (
                                    <PopoverMenu title={t('Status')} icon={<FilterList />}>
                                        <FormLabel sx={{ pl: 1.5 }}>{t('Status Filter')}</FormLabel>
                                        <FormGroup>
                                            {statusFilterOptions.map((option) => (
                                                <FormControlLabel
                                                    key={option}
                                                    control={<Checkbox />}
                                                    checked={searchForm.f_ed_status.includes(option)}
                                                    label={t(option)}
                                                    value={option}
                                                    onChange={handleStatusFilterChange}
                                                    sx={{ m: 0 }}
                                                />
                                            ))}
                                        </FormGroup>
                                    </PopoverMenu>
                                )}
                            </Box>
                        </div>
                        {tableElement}
                    </div>
                </BaseCard>
                <DeviceDetailDialog
                    open={deviceDetailDialogOpen}
                    deviceId={deviceId}
                    type={viewType as 'ap' | 'rt' | 'tag'}
                    formatUtcToStoreTime={formatUtcToStoreTime}
                    onClose={() => {
                        setDeviceDetailDialogOpen(false);
                        setViewType('');
                    }}
                />
            </BasePaper>
        </>
    );
}
